import { useState } from 'react';
import { Button, Icon, Input, SegmentedControl, Sheet } from '@astral/ui';
import { useT } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { useEmojiPresets } from '../hooks/useEmojiPresets.js';
import { EmojiBoard } from './EmojiBoard.js';
import { EmojiSequenceEditor } from './EmojiSequenceEditor.js';

type Mode = 'same' | 'sequence' | 'preset';

export interface BulkEmojiEditorProps {
  open: boolean;
  /** Number of target stickers (selected, or the whole pack). */
  count: number;
  onClose: () => void;
  /** Apply a single emoji to every target sticker. */
  onSame: (emoji: string) => void;
  /** Apply an ordered emoji sequence cyclically across the target stickers. */
  onSequence: (emojis: string[]) => void;
  /** Open the full preset manager (parent closes this sheet first). */
  onManagePresets: () => void;
}

/**
 * Compact bulk emoji editor with three modes: one emoji for all, a repeating
 * emoji sequence, or a saved preset. Sequences repeat across the target
 * stickers in their current visual order.
 */
export function BulkEmojiEditor({
  open,
  count,
  onClose,
  onSame,
  onSequence,
  onManagePresets,
}: BulkEmojiEditorProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { presets, create } = useEmojiPresets(open);
  const [mode, setMode] = useState<Mode>('same');
  const [sequence, setSequence] = useState<string[]>([]);
  const [saveName, setSaveName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  const applySequence = (emojis: string[]): void => {
    if (emojis.length === 0) return;
    onSequence(emojis);
  };

  const saveAsPreset = async (): Promise<void> => {
    if (saveName.trim().length === 0 || sequence.length === 0) return;
    setSavingPreset(true);
    const created = await create({ name: saveName.trim(), emojis: sequence });
    setSavingPreset(false);
    if (created) {
      setSaveName('');
      toast.show(t('presets.created'), 'success');
    } else {
      toast.show(t('presets.saveFailed'), 'error');
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('stickers.bulkTitle', { count })}>
      <div className="stack" style={{ gap: 14 }}>
        <SegmentedControl<Mode>
          value={mode}
          onChange={setMode}
          options={[
            { value: 'same', label: t('stickers.bulkSame') },
            { value: 'sequence', label: t('stickers.bulkSequence') },
            { value: 'preset', label: t('stickers.bulkPreset') },
          ]}
        />

        {mode === 'same' && (
          <div className="stack" style={{ gap: 8 }}>
            <p className="muted" style={{ fontSize: 12 }}>{t('stickers.bulkSameDesc')}</p>
            <EmojiBoard onSelect={onSame} />
          </div>
        )}

        {mode === 'sequence' && (
          <div className="stack" style={{ gap: 12 }}>
            <p className="muted" style={{ fontSize: 12 }}>{t('stickers.bulkSequenceDesc')}</p>
            <EmojiSequenceEditor emojis={sequence} onChange={setSequence} />
            <Button
              fullWidth
              size="lg"
              disabled={sequence.length === 0}
              leftIcon={<Icon name="sparkles" size={18} />}
              onClick={() => applySequence(sequence)}
            >
              {t('stickers.applySequence')}
            </Button>
            {sequence.length > 0 && (
              <div className="row-between" style={{ gap: 8 }}>
                <Input
                  placeholder={t('presets.namePlaceholder')}
                  value={saveName}
                  maxLength={40}
                  onChange={(e) => setSaveName(e.target.value)}
                />
                <Button
                  variant="secondary"
                  loading={savingPreset}
                  disabled={saveName.trim().length === 0}
                  onClick={() => void saveAsPreset()}
                >
                  {t('presets.saveAs')}
                </Button>
              </div>
            )}
          </div>
        )}

        {mode === 'preset' && (
          <div className="stack" style={{ gap: 10 }}>
            <p className="muted" style={{ fontSize: 12 }}>{t('stickers.bulkPresetDesc')}</p>
            {presets.length === 0 ? (
              <p className="muted">{t('presets.empty')}</p>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {presets.map((p) => (
                  <div key={p.id} className="preset-row">
                    <div className="preset-row__main">
                      <span className="preset-row__name">{p.name}</span>
                      <span className="preset-row__emojis">{p.emojis.join(' ')}</span>
                    </div>
                    <Button size="sm" onClick={() => applySequence(p.emojis)}>
                      {t('presets.apply')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button variant="ghost" leftIcon={<Icon name="edit" size={16} />} onClick={onManagePresets}>
              {t('presets.manage')}
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
